import { AlertTriangle, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Section } from "@/shared/ui/section"

import { extractErrorMessage } from "@/shared/lib"
import { cn } from "@/shared/lib/utils"

interface QueryErrorProps extends React.ComponentProps<"section"> {
  error: unknown
  message?: string
  /** Offers a retry; leave it out where asking again cannot help. */
  onRetry?: () => void
}

export const QueryError: React.FC<QueryErrorProps> = ({
  error,
  message: localizedMessage,
  onRetry,
  className,
  ...props
}) => {
  const { t } = useTranslation()
  const message = localizedMessage ?? (error ? extractErrorMessage(error) : undefined)

  return (
    <Section.Root
      {...props}
      role="alert"
      className={cn(
        "min-h-[200px] place-content-center place-items-center rounded-2xl bg-gray-800 p-6 text-center",
        className
      )}>
      <header>
        <AlertTriangle
          aria-hidden="true"
          focusable="false"
          className="mx-auto size-8 text-amber-400/80"
        />
        <Section.Label
          as="h3"
          className="mt-3 text-[0.9375rem] font-semibold text-gray-100">
          {t("query-error-title", "Failed to load data")}
        </Section.Label>
      </header>
      <Section.Description className="mt-1.5 max-w-xs self-center break-words text-center text-sm text-gray-400 empty:hidden">
        {message}
      </Section.Description>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-gray-700 px-4 text-sm font-medium text-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-400 hover-device:hover:bg-gray-600">
          <RefreshCw
            aria-hidden="true"
            focusable="false"
            className="size-4"
          />
          {t("common:error-boundary-try-again", "Try again")}
        </button>
      ) : null}
    </Section.Root>
  )
}
